import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["charterTypeSelect", "duration", "boardingTime", "departureTime", "addOnsContainer", "customFieldsContainer"]
  static values = { durationOptions: Array, timeslotOptions: Array, addOns: Object }

  connect() {
    // Remember the type the server rendered so custom field rows it built
    // (persisted values, or values re-rendered after a validation error)
    // survive connect() and are only rebuilt when the type actually changes.
    this.renderedTypeId = this.hasCharterTypeSelectTarget ? this.charterTypeSelectTarget.value : ""
    this.updatePricingFields()
  }

  updatePricingFields() {
    if (!this.hasCharterTypeSelectTarget) {
      // No charter types - show both fields
      this.showBothFields()
      this.updateAddOns(null)
      this.updateCustomFields(null)
      return
    }

    const select = this.charterTypeSelectTarget
    const selectedOption = select.options[select.selectedIndex]

    if (!selectedOption || !selectedOption.value) {
      // No selection - show both fields
      this.showBothFields()
      this.updateAddOns(null)
      this.updateCustomFields(null)
      return
    }

    // Update add-ons for the selected charter type
    this.updateAddOns(selectedOption.value)

    // Update custom fields for the selected charter type
    this.updateCustomFields(selectedOption)

    const minDuration = parseInt(selectedOption.dataset.minDuration) || 0
    const maxDuration = parseInt(selectedOption.dataset.maxDuration) || 10000
    const startTime = parseInt(selectedOption.dataset.startTime) || 0
    const endTime = parseInt(selectedOption.dataset.endTime) || 2345

    // Update timeslot options based on charter type constraints
    this.updateTimeslotOptions(startTime, endTime)

    // Update duration options based on charter type constraints
    this.updateDurationOptions(minDuration, maxDuration)
  }

  updateDurationOptions(minDuration, maxDuration) {
    if (!this.hasDurationTarget || !this.hasDurationOptionsValue) return

    const currentValue = this.durationTarget.value
    const allOptions = this.durationOptionsValue

    // Filter options within range
    const filteredOptions = allOptions.filter(([label, value]) => {
      return value >= minDuration && value <= maxDuration
    })

    // Rebuild select
    this.durationTarget.innerHTML = ''

    // Add blank option if multiple choices
    if (filteredOptions.length !== 1) {
      const blank = document.createElement('option')
      blank.value = ''
      blank.textContent = 'Select duration'
      this.durationTarget.appendChild(blank)
    }

    // Add filtered options
    let currentValueStillValid = false
    filteredOptions.forEach(([label, value]) => {
      const option = document.createElement('option')
      option.value = value
      option.textContent = label
      this.durationTarget.appendChild(option)
      if (String(value) === String(currentValue)) {
        currentValueStillValid = true
      }
    })

    // Restore or auto-select value
    if (filteredOptions.length === 1) {
      this.durationTarget.value = filteredOptions[0][1]
    } else if (currentValueStillValid) {
      this.durationTarget.value = currentValue
    } else {
      this.durationTarget.value = ''
    }
  }

  showBothFields() {
    // Reset timeslots to show all options
    if (this.hasTimeslotOptionsValue) {
      this.updateTimeslotOptions(0, 2345)
    }
    // Reset duration to show all options
    if (this.hasDurationTarget && this.hasDurationOptionsValue) {
      this.updateDurationOptions(0, 10000)
    }
  }

  updateTimeslotOptions(startTime, endTime) {
    if (!this.hasTimeslotOptionsValue) return

    const allOptions = this.timeslotOptionsValue

    // Filter options within range
    const filteredOptions = allOptions.filter(([label, value]) => {
      return value >= startTime && value <= endTime
    })

    // Update boarding time
    if (this.hasBoardingTimeTarget) {
      this.rebuildTimeslotSelect(this.boardingTimeTarget, filteredOptions, 'Select a time')
    }

    // Update departure time
    if (this.hasDepartureTimeTarget) {
      this.rebuildTimeslotSelect(this.departureTimeTarget, filteredOptions, 'Select a time')
    }
  }

  rebuildTimeslotSelect(selectElement, options, blankText) {
    const currentValue = selectElement.value

    selectElement.innerHTML = ''

    // Add blank option
    const blank = document.createElement('option')
    blank.value = ''
    blank.textContent = blankText
    selectElement.appendChild(blank)

    // Add filtered options
    let currentValueStillValid = false
    options.forEach(([label, value]) => {
      const option = document.createElement('option')
      option.value = value
      option.textContent = label
      selectElement.appendChild(option)
      if (String(value) === String(currentValue)) {
        currentValueStillValid = true
      }
    })

    // Restore value if still valid
    if (currentValueStillValid) {
      selectElement.value = currentValue
    }
  }

  updateCustomFields(selectedOption) {
    if (!this.hasCustomFieldsContainerTarget) return

    const container = this.customFieldsContainerTarget

    // Keep the rows the server rendered until the user picks a different type.
    const selectedId = selectedOption ? selectedOption.value : ""
    if (selectedId === this.renderedTypeId) return
    this.renderedTypeId = null

    const prefix = container.dataset.fieldNamePrefix
    if (!prefix) return

    let templates = []
    try {
      templates = JSON.parse(selectedOption?.dataset.customFieldNames || "[]")
    } catch (_e) {
      templates = []
    }

    // Index the current rows by field name so a field the new type shares
    // keeps its value (and its id, when persisted).
    const existingRows = new Map()
    container.querySelectorAll(".custom-field-row").forEach(row => {
      const name = row.querySelector('input[name$="[name]"]')?.value
      if (name && !existingRows.has(name)) existingRows.set(name, row)
    })

    container.innerHTML = ""
    let index = 0

    templates.forEach(tpl => {
      const row = existingRows.get(tpl.name)
      if (row) {
        existingRows.delete(tpl.name)
        this.reviveCustomFieldRow(row)
        this.renumberCustomFieldRow(row, prefix, index)
        container.appendChild(row)
      } else {
        container.appendChild(this.buildCustomFieldRow(prefix, index, tpl.name))
      }
      index++
    })

    // Rows the new type doesn't have: persisted ones stay in the form hidden
    // and marked for destruction so saving removes them; unsaved ones are dropped.
    existingRows.forEach(row => {
      if (!row.querySelector('input[name$="[id]"]')) return
      row.hidden = true
      const destroy = document.createElement("input")
      destroy.type = "hidden"
      destroy.name = `${prefix}[0][_destroy]`
      destroy.value = "1"
      row.appendChild(destroy)
      this.renumberCustomFieldRow(row, prefix, index)
      container.appendChild(row)
      index++
    })
  }

  // Undo a previous "drop" so a row reappears when its type is selected again.
  reviveCustomFieldRow(row) {
    row.hidden = false
    row.querySelector('input[name$="[_destroy]"]')?.remove()
  }

  renumberCustomFieldRow(row, prefix, index) {
    row.querySelectorAll("input").forEach(input => {
      input.name = input.name.replace(/\[\d+\](\[\w+\])$/, `[${index}]$1`)
      if (input.id) input.id = input.id.replace(/_\d+_(\w+)$/, `_${index}_$1`)
    })
    const label = row.querySelector("label")
    const text = row.querySelector('input[type="text"]')
    if (label && text) label.htmlFor = text.id
  }

  buildCustomFieldRow(prefix, index, name) {
    const row = document.createElement("div")
    row.className = "custom-field-row mb-3"

    const fieldId = `${prefix.replace(/[\[\]]/g, "_")}_${index}_value`.replace(/_+/g, "_")

    const label = document.createElement("label")
    label.className = "form-label fw-semibold mb-1"
    label.htmlFor = fieldId
    label.textContent = name

    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = `${prefix}[${index}][name]`
    hidden.value = name

    const input = document.createElement("input")
    input.type = "text"
    input.className = "form-control"
    input.placeholder = "Value"
    input.name = `${prefix}[${index}][value]`
    input.id = fieldId

    row.appendChild(label)
    row.appendChild(hidden)
    row.appendChild(input)
    return row
  }

  updateAddOns(charterTypeId) {
    if (!this.hasAddOnsContainerTarget) return

    this.addOnsContainerTarget.innerHTML = ''

    if (!charterTypeId || !this.hasAddOnsValue) return

    const addOns = this.addOnsValue[charterTypeId] || []
    if (addOns.length === 0) return

    // Group add-ons by package
    const groupedByPackage = addOns.reduce((groups, addOn) => {
      const pkg = addOn.package_label
      if (!groups[pkg]) groups[pkg] = []
      groups[pkg].push(addOn)
      return groups
    }, {})

    // Render checkboxes grouped by package
    Object.entries(groupedByPackage).forEach(([packageLabel, items]) => {
      const packageDiv = document.createElement('div')
      packageDiv.className = 'mb-3'

      const label = document.createElement('label')
      label.className = 'form-label fw-semibold text-muted small'
      label.textContent = packageLabel
      packageDiv.appendChild(label)

      items.forEach(addOn => {
        const checkDiv = document.createElement('div')
        checkDiv.className = 'form-check'

        const input = document.createElement('input')
        input.type = 'checkbox'
        input.className = 'form-check-input'
        input.name = 'private_charter[add_on_item_ids][]'
        input.value = addOn.id
        input.id = `add_on_item_${addOn.id}`
        input.dataset.amountPerGuest = addOn.amount_per_guest
        input.dataset.flatRateAmount = addOn.flat_rate_amount
        input.dataset.action = "change->charter-financial#calculateFromInputs"
        input.dataset.charterFinancialTarget = "addOnItem"

        const checkLabel = document.createElement('label')
        checkLabel.className = 'form-check-label'
        checkLabel.htmlFor = `add_on_item_${addOn.id}`

        // Format price display
        let priceDisplay = ''
        if (addOn.amount_per_guest > 0) {
          priceDisplay = `$${addOn.amount_per_guest.toFixed(2)}/guest`
        } else if (addOn.flat_rate_amount > 0) {
          priceDisplay = `$${addOn.flat_rate_amount.toFixed(2)} flat`
        }

        checkLabel.innerHTML = `${addOn.name} <span class="text-muted small ms-1">(${priceDisplay})</span>`

        checkDiv.appendChild(input)
        checkDiv.appendChild(checkLabel)
        packageDiv.appendChild(checkDiv)
      })

      this.addOnsContainerTarget.appendChild(packageDiv)
    })
  }
}
